import { useState, useMemo } from "react";

import { DateTimePicker, DialogWrapper, LabelWrapper } from "@bridgesplit/ui";
import { AppDialog, useAppDialog } from "app/utils";
import { BalanceDisplay, DialogHeaderWithBack, TokenInputWithPrice, useBalanceChecker } from "app/components/common";
import { LendingVaultExpanded, useStrategyDurations } from "@bridgesplit/abf-react";

import { ManagerCreateRewardsCta } from "./ManagerCreateRewardsCta";
import { CollateralSelect } from "../common/CollateralSelect";
import { CreateRewardForm } from "../common/types";
import { ManagerViewProvider } from "../../ManagerViewContext";
const DEFAULT_DURATION_STAKE_WEIGHTS = [1, 1, 1, 1, 1];

const initialForm: CreateRewardForm = {
    vaultAddress: undefined,
    rewardToken: undefined,
    rewardAmount: undefined,
    rewardStartTime: null,
    rewardEndTime: null,
    durationStakeWeights: DEFAULT_DURATION_STAKE_WEIGHTS
};

enum RewardStep {
    CollateralSelect = "CollateralSelect",
    RewardConfig = "RewardConfig"
}

const REWARD_STEPS = [RewardStep.CollateralSelect, RewardStep.RewardConfig];

export interface ManagerAddRewardsProps {
    form: CreateRewardForm;
    setForm: (form: CreateRewardForm) => void;
    vaultExpanded: LendingVaultExpanded | undefined;
    setStep: (step: RewardStep) => void;
}
export function ManagerAddRewardsDialog() {
    const [form, setForm] = useState<CreateRewardForm>(initialForm);
    const [step, setStep] = useState<RewardStep>(RewardStep.CollateralSelect);
    const { strategyDurations } = useStrategyDurations();

    const { getData } = useAppDialog();
    const data = getData(AppDialog.ManagerAddRewards);

    const stepIndex = REWARD_STEPS.indexOf(step);
    const content = useMemo(() => {
        const formProps: ManagerAddRewardsProps = {
            form,
            setForm,
            vaultExpanded: data?.vaultExpanded,
            setStep
        };

        switch (step) {
            case RewardStep.CollateralSelect:
                return (
                    <CollateralSelect<RewardStep, CreateRewardForm> {...formProps} nextStep={RewardStep.RewardConfig} />
                );
            case RewardStep.RewardConfig:
                return <RewardConfig {...formProps} />;
            default:
                return null;
        }
    }, [step, form, data?.vaultExpanded, setStep]);

    return (
        <ManagerViewProvider vaultExpanded={data?.vaultExpanded} strategyDurations={strategyDurations}>
            <DialogWrapper maxWidth={500}>
                <DialogHeaderWithBack
                    back={stepIndex > 0 ? () => setStep(REWARD_STEPS[stepIndex - 1]) : undefined}
                    header="Add Rewards"
                />
                {content}
                {stepIndex === REWARD_STEPS.length - 1 ? (
                    <ManagerCreateRewardsCta
                        form={form}
                        setForm={setForm}
                        vaultExpanded={data?.vaultExpanded}
                        setStep={setStep}
                    />
                ) : null}
            </DialogWrapper>
        </ManagerViewProvider>
    );
}

function RewardConfig({ form, setForm, setStep }: ManagerAddRewardsProps) {
    const { balance, insufficientBalance, isLoading } = useBalanceChecker({
        mint: form.rewardToken?.mint,
        amount: form.rewardAmount
    });

    return (
        <>
            <LabelWrapper label="Reward Token">
                <TokenInputWithPrice
                    selectToken={() => setStep(RewardStep.CollateralSelect)}
                    metadata={form?.rewardToken}
                    value={form?.rewardAmount}
                    setValue={(val: number | undefined) => {
                        setForm({
                            ...form,
                            rewardAmount: val
                        });
                    }}
                    belowInput={
                        <BalanceDisplay
                            balance={balance}
                            insufficientBalance={insufficientBalance}
                            isLoading={isLoading}
                            setMax={(amount) => setForm({ ...form, rewardAmount: amount })}
                            metadata={form?.rewardToken}
                        />
                    }
                />
            </LabelWrapper>
            <LabelWrapper
                label="Start Time"
                tooltip="The time when the rewards will start being emitted, shown in local time"
            >
                <DateTimePicker
                    value={form?.rewardStartTime}
                    onChange={(val) => setForm({ ...form, rewardStartTime: val })}
                    sx={{
                        width: "100%"
                    }}
                />
            </LabelWrapper>
            <LabelWrapper
                label="End Time"
                tooltip="The time when the rewards will stop being emitted, shown in local time"
            >
                <DateTimePicker
                    value={form?.rewardEndTime}
                    onChange={(val) => setForm({ ...form, rewardEndTime: val })}
                    sx={{
                        width: "100%"
                    }}
                />
            </LabelWrapper>
        </>
    );
}
